import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useNavigate, useLocation, Outlet, Link } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Container,
  Divider,
  useTheme,
  Tooltip,
  Zoom,
  Fade,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  DirectionsCar as CarIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  BarChart as ChartIcon,
  AccountBalance as CaixaIcon,
  AccountBalanceWallet as ConciliacaoIcon,
  SwapHoriz as MovimentacoesIcon,
  Description as ContractIcon,
  Notifications as NotificationsIcon,
  AccountCircle as UserIcon,
  Speed as SpeedIcon,
  CarCrash as CarCrashIcon,
  LocalGasStation as GasIcon,
  PeopleAlt as LeadsIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  AdminPanelSettings as AdminIcon,
  Logout as LogoutIcon,
  People as PeopleIcon,
  Public as PublicIcon,
  Assessment as AssessmentIcon,
  CarRental as CarRentalIcon,
  RequestQuote as ProposalIcon,
} from '@mui/icons-material';
import { animated, useSpring } from '@react-spring/web';
import StorageIcon from '@mui/icons-material/Storage';
import HistoryIcon from '@mui/icons-material/History';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CampaignIcon from '@mui/icons-material/Campaign';
import { ColorModeContext } from '../App';
import saleService from '../services/saleService';
import NotificationCenter from './NotificationCenter';
import authService from '../services/authService';
import { AuthUserProvider } from '../contexts/AuthUserContext';
import { getStoreRoleLabel, isStoreAdmin } from '../utils/storeRoles';

const drawerWidth = 260;
const AnimatedListItemButton = animated(ListItemButton);

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [selectedAdminIndex, setSelectedAdminIndex] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const [countDisponiveis, setCountDisponiveis] = useState<string | null>(null);
  const [countManutencao, setCountManutencao] = useState<string | null>(null);

  const [role, setRole] = useState<string | null>(null);
  const [userDisplayName, setUserDisplayName] = useState<string>('');


  useEffect(() => {
    const fetchData = async () => {
      try {
        // Busca resumo da frota (veículos disponíveis e em manutenção)
        const retorno = await saleService.getSummaryFrota();
        
        if (retorno) {
          setCountDisponiveis(retorno.available || '0');
          setCountManutencao(retorno.maintenance || '0');
        }
      } catch (error: any) {
        // Se o endpoint não existir (404) ou houver outro erro, usa valores padrão
        console.warn('Endpoint /frota-summary não disponível ou erro ao buscar:', error?.response?.status || error?.message);
        setCountDisponiveis('0');
        setCountManutencao('0');
      }

      try {
        // Busca informações do usuário logado
        const retornoMe = await authService.me();
        if (retornoMe && retornoMe.user) {
          setRole(retornoMe.user.role);
          setUserDisplayName(retornoMe.user.name || '');
        }
      } catch (error: any) {
        console.warn('Erro ao buscar informações do usuário:', error?.response?.status || error?.message);
      }
    };

    fetchData();
  }, []);

  // Animação para o conteúdo principal
  const contentProps = useSpring({
    opacity: 1,
    transform: 'translateY(0)',
    from: { opacity: 0, transform: 'translateY(20px)' },
    config: { tension: 280, friction: 20 }
  });

  const allMenuItems = useMemo(() => [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', bgColor: theme.palette.primary.main, adminOnly: true },
    { text: 'Veículos', icon: <CarIcon />, path: '/veiculos', bgColor: theme.palette.secondary.main, adminOnly: false },
    { text: 'Despesas', icon: <MoneyIcon />, path: '/despesas', bgColor: theme.palette.error.main, adminOnly: true },
    { text: 'Vendas', icon: <ReceiptIcon />, path: '/vendas', bgColor: theme.palette.success.main, adminOnly: false },
    { text: 'Locações', icon: <CarRentalIcon />, path: '/locacoes', bgColor: '#6a1b9a', adminOnly: false },
    { text: 'Propostas', icon: <ProposalIcon />, path: '/propostas', bgColor: '#7c3aed', adminOnly: false },
    { text: 'Relatórios', icon: <ChartIcon />, path: '/relatorios', bgColor: theme.palette.info.main, adminOnly: true },
    { text: 'DRE', icon: <AssessmentIcon />, path: '/dre', bgColor: '#1565c0', adminOnly: true },
    { text: 'Caixa', icon: <CaixaIcon />, path: '/caixa', bgColor: '#5e35b1', adminOnly: true },
    { text: 'Conciliação', icon: <ConciliacaoIcon />, path: '/conciliacao', bgColor: '#00838f', adminOnly: true },
    { text: 'Movimentações', icon: <MovimentacoesIcon />, path: '/movimentacoes', bgColor: '#00897b', adminOnly: true },
    { text: 'Contratos', icon: <ContractIcon />, path: '/contratos', bgColor: theme.palette.warning.main, adminOnly: false },
    { text: 'Leads', icon: <LeadsIcon />, path: '/leads', bgColor: '#2563eb', adminOnly: false },
    { text: 'Acessos da Vitrine', icon: <StorefrontIcon />, path: '/acessos-vitrine', bgColor: '#DD2F30', adminOnly: true },
    { text: 'Funcionários', icon: <PeopleIcon />, path: '/funcionarios', bgColor: '#00796b', adminOnly: true },
  ], [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.error.main,
  theme.palette.success.main, theme.palette.info.main, theme.palette.warning.main]);

  const menuItems = useMemo(() => {
    const admin = isStoreAdmin(role);
    return allMenuItems.filter((item) => admin || !item.adminOnly);
  }, [allMenuItems, role]);

  const subdomain = window.location.hostname.split('.')[0];

  const adminMenuItems = useMemo(() => {
    const items = [
      { text: 'Usuários', icon: <AdminIcon />, path: '/usuarios', bgColor: theme.palette.warning.main },
      { text: 'Atividades', icon: <HistoryIcon />, path: '/registro-atividades', bgColor: theme.palette.warning.main },
      { text: 'Integração OLX', icon: <CampaignIcon />, path: '/integracoes/olx', bgColor: theme.palette.warning.main },
      { text: 'Databases', icon: <StorageIcon />, path: '/bancos', bgColor: theme.palette.warning.main },
    ];

    //**Logica para remover menus com base na regra do usuarios */
    if (subdomain !== 'admin') {
      return items.filter(item => item.path !== '/bancos');
    }

    return items;
  }, [theme.palette.warning.main, subdomain]);

  useEffect(() => {
    // Verificar primeiro no menu principal (evita que '/' coincida com todas as rotas)
    const mainIndex = menuItems.findIndex((item) => {
      if (item.path === '/') return location.pathname === '/';
      return location.pathname.startsWith(item.path);
    });
    if (mainIndex !== -1) {
      setSelectedIndex(mainIndex);
      setSelectedAdminIndex(null);
      return;
    }

    // Se não encontrou no menu principal, verificar no menu de administração
    const adminIndex = adminMenuItems.findIndex((item) => {
      if (item.path === '/') return location.pathname === '/';
      return location.pathname.startsWith(item.path);
    });
    if (adminIndex !== -1) {
      setSelectedIndex(null); // Remove a seleção do menu principal
      setSelectedAdminIndex(adminIndex);
      return;
    }

    // Se não encontrou em nenhum menu, limpar a seleção
    setSelectedIndex(null);
    setSelectedAdminIndex(null);
  }, [location.pathname, menuItems, adminMenuItems]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const drawer = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, #1a1a1a 30%, #121212 100%)'
          : 'linear-gradient(180deg, #ffffff 30%, #f5f5f5 100%)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          background: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
          minHeight: 72,
          borderBottom: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
        }}
      >
        <img
          src={`${process.env.PUBLIC_URL || ''}/assets/logos/LOGO - R MOTORS vertical 001.png`}
          alt="R Motors"
          style={{ maxHeight: 44, width: 'auto', objectFit: 'contain' }}
          onError={(e) => {
            const el = e.target as HTMLImageElement;
            el.style.display = 'none';
            const next = el.nextElementSibling as HTMLElement;
            if (next) next.style.display = 'block';
          }}
        />
        <Typography component="span" variant="h5" fontWeight="bold" sx={{ color: theme.palette.mode === 'dark' ? '#F00000' : '#fff', display: 'none' }}>
          R Motors
        </Typography>
      </Box>
      <Divider sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />
      <Box sx={{ overflow: 'auto', flex: 1, pt: 2 }}>
        <List>
          {menuItems.map((item, index) => (
            <Zoom
              in={true}
              key={item.text}
              style={{ transitionDelay: `${index * 50}ms` }}
              unmountOnExit

            >
              <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>

                <AnimatedListItemButton
                  style={{
                    opacity: 1,
                    transform: 'translateY(0)',
                  }}
                  onClick={() => navigate(item.path)}
                  selected={item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)}
                  sx={{
                    borderRadius: '0 24px 24px 0',
                    mx: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: '0.3s',
                    '&.Mui-selected': {
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(240, 0, 0, 0.15)'
                        : 'rgba(240, 0, 0, 0.1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        backgroundColor: item.bgColor,
                        borderRadius: '0 4px 4px 0',
                      },
                    },
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(240, 0, 0, 0.08)'
                        : 'rgba(240, 0, 0, 0.05)',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: location.pathname === item.path ? item.bgColor : 'text.secondary',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: location.pathname === item.path ? 'bold' : 'normal'
                    }}
                  />
                </AnimatedListItemButton>

              </ListItem>

            </Zoom>
          ))}
        </List>
      </Box>
      {isStoreAdmin(role) && (
        <>
      
      <Divider sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />
      <Box sx={{ overflow: 'auto', py: 2 }}>
        <Typography
          variant="caption"
          sx={{
            pl: 3,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontWeight: 'bold',
            color: theme.palette.text.secondary,
            display: 'block',
            mb: 1
          }}
        >
          Administração
        </Typography>
        <List>
          {adminMenuItems.map((item, index) => (
            <Zoom
              in={true}
              key={item.text}
              style={{ transitionDelay: `${(menuItems.length + index) * 50}ms` }}
              unmountOnExit
            >
              <ListItem disablePadding sx={{ mb: 1 }}>
                <AnimatedListItemButton
                  style={{
                    opacity: 1,
                    transform: 'translateY(0)',
                  }}
                  onClick={() => navigate(item.path)}
                  selected={item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)}
                  sx={{
                    borderRadius: '0 24px 24px 0',
                    mx: 1,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: '0.3s',
                    '&.Mui-selected': {
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(240, 0, 0, 0.15)'
                        : 'rgba(240, 0, 0, 0.1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        backgroundColor: item.bgColor,
                        borderRadius: '0 4px 4px 0',
                      },
                    },
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(240, 0, 0, 0.08)'
                        : 'rgba(240, 0, 0, 0.05)',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: location.pathname === item.path ? item.bgColor : 'text.secondary',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: location.pathname === item.path ? 'bold' : 'normal'
                    }}
                  />
                </AnimatedListItemButton>
              </ListItem>
            </Zoom>
          ))}
        </List>
      </Box>
      </>
)}
      <Divider sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: theme.palette.mode === 'dark'
            ? 'rgba(30, 30, 30, 0.7)'
            : 'rgba(245, 245, 245, 0.7)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            sx={{
              bgcolor: theme.palette.primary.main,
              width: 36,
              height: 36,
              boxShadow: `0 0 10px ${theme.palette.primary.main}50`,
            }}
          >
            <UserIcon />
          </Avatar>
          <Box sx={{ ml: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              {userDisplayName || 'Usuário'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getStoreRoleLabel(role)}
            </Typography>
          </Box>
        </Box>
        <NotificationCenter />
      </Box>

    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: theme.palette.mode === 'dark' ? '#1e1e1e' : '#fafafa',
          borderBottom: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {selectedIndex !== null && (
              <React.Fragment>
                <Fade in={true} timeout={800}>
                  <Box
                    sx={{
                      mr: 2,
                      p: 0.5,
                      borderRadius: '50%',
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.05)'
                    }}
                  >
                    {menuItems[selectedIndex].icon}
                  </Box>
                </Fade>
                <Fade in={true} timeout={1000}>
                  <Typography
                    variant="h6"
                    noWrap
                    component="div"
                    sx={{
                      color: theme.palette.mode === 'dark' ? 'inherit' : '#333333'
                    }}
                  >
                    {menuItems[selectedIndex].text}
                  </Typography>
                </Fade>
              </React.Fragment>
            )}

            {selectedAdminIndex !== null && (
              <React.Fragment>
                <Fade in={true} timeout={800}>
                  <Box
                    sx={{
                      mr: 2,
                      p: 0.5,
                      borderRadius: '50%',
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.05)'
                    }}
                  >
                    {adminMenuItems[selectedAdminIndex].icon}
                  </Box>
                </Fade>
                <Fade in={true} timeout={1000}>
                  <Typography
                    variant="h6"
                    noWrap
                    component="div"
                    sx={{
                      color: theme.palette.mode === 'dark' ? 'inherit' : '#333333'
                    }}
                  >
                    {adminMenuItems[selectedAdminIndex].text}
                  </Typography>
                </Fade>
              </React.Fragment>
            )}
          </Box>

          <Box sx={{ display: 'flex', ml: 'auto', alignItems: 'center' }}>
            <Tooltip title="Ver vitrine pública" arrow>
              <IconButton component={Link} to="/vitrine" target="_blank" rel="noopener noreferrer" color="inherit" sx={{ mr: 1, opacity: 0.85 }}>
                <PublicIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={theme.palette.mode === 'dark' ? 'Mudar para modo claro' : 'Mudar para modo escuro'} arrow>
              <IconButton onClick={colorMode.toggleColorMode} color="inherit" sx={{ mr: 1 }}>
                {theme.palette.mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Status da Frota" arrow>
              <IconButton color="inherit" sx={{ mr: 1 }}>
                <Badge badgeContent={countDisponiveis} color="success">
                  <CarIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title="Manutenção" arrow>
              <IconButton color="inherit" sx={{ mr: 1 }}>
                <Badge badgeContent={countManutencao ? countManutencao : '0'} color="warning">
                  <CarCrashIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title="Sair" TransitionComponent={Zoom}>
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: theme.palette.mode === 'dark' ? 'white' : '#333',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.1)'
                      : 'rgba(0,0,0,0.1)',
                  },
                }}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { sm: drawerWidth },
          flexShrink: { sm: 0 },
          position: 'fixed', // Fixar a barra lateral
          height: '100vh',
          zIndex: theme.zIndex.drawer,
        }}
        aria-label="menu items"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              position: 'fixed',
              border: 'none',
              borderRight: theme.palette.mode === 'dark'
                ? '1px solid rgba(255,255,255,0.05)'
                : '1px solid rgba(0,0,0,0.08)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component={animated.div}
        style={contentProps}
        sx={{
          flexGrow: 1,
          p: 3,
          width: '100%',
          minHeight: '100vh',
          marginLeft: { xs: 0, sm: `${drawerWidth}px` }, // Usando marginLeft apenas para espaçamento
          background: theme.palette.background.default,
          backgroundImage: theme.palette.mode === 'dark'
            ? `
                radial-gradient(circle at 25px 25px, rgba(240, 0, 0, 0.15) 2px, transparent 0),
                radial-gradient(circle at 75px 75px, rgba(240, 0, 0, 0.1) 2px, transparent 0)
              `
            : `
                radial-gradient(circle at 25px 25px, rgba(240, 0, 0, 0.08) 2px, transparent 0),
                radial-gradient(circle at 75px 75px, rgba(240, 0, 0, 0.05) 2px, transparent 0)
              `,
          backgroundSize: '100px 100px',
        }}
      >
        <Toolbar /> {/* Espaçamento para o AppBar */}
        <AuthUserProvider role={role}>
          <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Outlet />
          </Container>
        </AuthUserProvider>
      </Box>
    </Box>
  );
};

export default Layout; 